import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '../lib/api-client';
import PublicContentPage, { PublicCtaLink } from '../components/layout/PublicContentPage';

const PARTNER_CONTACT_HREF = 'mailto:sales@ablecomply.com?subject=AbleComply%20Partner%20Program';

interface FormState {
  name: string;
  company: string;
  email: string;
  websiteUrl: string;
  linkedinUrl: string;
  country: string;
  partnerType: string;
  audienceDescription: string;
  howTheyPlanToSell: string;
  notes: string;
}

interface FieldErrors {
  name: string | null;
  company: string | null;
  email: string | null;
  websiteUrl: string | null;
  linkedinUrl: string | null;
}

function emptyForm(): FormState {
  return {
    name: '',
    company: '',
    email: '',
    websiteUrl: '',
    linkedinUrl: '',
    country: '',
    partnerType: '',
    audienceDescription: '',
    howTheyPlanToSell: '',
    notes: '',
  };
}

function emptyErrors(): FieldErrors {
  return {
    name: null,
    company: null,
    email: null,
    websiteUrl: null,
    linkedinUrl: null,
  };
}

const ERROR_FIELDS: Array<keyof FieldErrors> = [
  'name',
  'company',
  'email',
  'websiteUrl',
  'linkedinUrl',
];

function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}

export default function PartnerApplyPage() {
  const { t } = useTranslation('marketing');
  const sidebarPoints = t('pages.partnersApply.sidebar.points', { returnObjects: true }) as string[];
  const partnerTypes = t('pages.partnersApply.form.partnerTypes', { returnObjects: true }) as Record<string, string>;

  const [form, setForm] = useState<FormState>(emptyForm);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>(emptyErrors);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const updateField = (field: keyof FormState) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const value = event.target.value;
    setForm((current) => ({ ...current, [field]: value }));
    setFormError(null);
    if (ERROR_FIELDS.includes(field as keyof FieldErrors)) {
      const errorField = field as keyof FieldErrors;
      setFieldErrors((current) => ({ ...current, [errorField]: null }));
    }
  };

  const validate = (): boolean => {
    const nextErrors = emptyErrors();
    let hasErrors = false;

    if (!form.name.trim()) {
      nextErrors.name = t('pages.partnersApply.errors.nameRequired');
      hasErrors = true;
    }

    if (!form.company.trim()) {
      nextErrors.company = t('pages.partnersApply.errors.companyRequired');
      hasErrors = true;
    }

    if (!form.email.trim()) {
      nextErrors.email = t('pages.partnersApply.errors.emailRequired');
      hasErrors = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      nextErrors.email = t('pages.partnersApply.errors.emailInvalid');
      hasErrors = true;
    }

    if (form.websiteUrl.trim() && !isValidUrl(form.websiteUrl.trim())) {
      nextErrors.websiteUrl = t('pages.partnersApply.errors.websiteInvalid');
      hasErrors = true;
    }

    if (form.linkedinUrl.trim() && !isValidUrl(form.linkedinUrl.trim())) {
      nextErrors.linkedinUrl = t('pages.partnersApply.errors.linkedinInvalid');
      hasErrors = true;
    }

    setFieldErrors(nextErrors);
    return !hasErrors;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (!validate()) {
      return;
    }

    setSubmitting(true);
    try {
      await api.post<{ accepted: true }>(
        '/partner-applications',
        {
          name: form.name.trim(),
          company: form.company.trim(),
          email: form.email.trim(),
          websiteUrl: form.websiteUrl.trim() || undefined,
          linkedinUrl: form.linkedinUrl.trim() || undefined,
          country: form.country.trim() || undefined,
          partnerType: form.partnerType || undefined,
          audienceDescription: form.audienceDescription.trim() || undefined,
          howTheyPlanToSell: form.howTheyPlanToSell.trim() || undefined,
          notes: form.notes.trim() || undefined,
        },
        { skipAuth: true },
      );

      setSubmitted(true);
      setForm(emptyForm());
      setFieldErrors(emptyErrors());
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message || t('pages.partnersApply.errors.submitFailed'));
      } else if (error instanceof TypeError) {
        setFormError(t('pages.partnersApply.errors.network'));
      } else {
        setFormError(t('pages.partnersApply.errors.submitFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicContentPage
      metaTitle={t('pages.partnersApply.meta.title')}
      metaDescription={t('pages.partnersApply.meta.description')}
      canonicalPath="/partners/apply"
      eyebrow={t('pages.partnersApply.hero.eyebrow')}
      title={t('pages.partnersApply.hero.title')}
      intro={t('pages.partnersApply.hero.intro')}
      aside={(
        <>
          <section className="acc-public-page-card acc-public-page-card--sticky">
            <h2>{t('pages.partnersApply.sidebar.title')}</h2>
            <p>{t('pages.partnersApply.sidebar.body')}</p>
            <ul className="acc-public-page-list">
              {sidebarPoints.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </section>

          <section className="acc-public-page-card">
            <h2>{t('pages.partnersApply.sidebar.processTitle')}</h2>
            <p>{t('pages.partnersApply.sidebar.processBody')}</p>
            <div className="acc-public-page-actions">
              <PublicCtaLink href={PARTNER_CONTACT_HREF} variant="ghost">
                {t('pages.partnersApply.sidebar.contactAction')}
              </PublicCtaLink>
            </div>
          </section>
        </>
      )}
    >
      {submitted && (
        <section className="acc-public-page-card acc-public-form-success">
          <h2>{t('pages.partnersApply.success.title')}</h2>
          <p>{t('pages.partnersApply.success.body')}</p>
          <div className="acc-public-page-actions">
            <PublicCtaLink href="/partners" variant="secondary">
              {t('pages.partnersApply.success.backAction')}
            </PublicCtaLink>
            <PublicCtaLink href={PARTNER_CONTACT_HREF} variant="ghost">
              {t('pages.partnersApply.success.contactAction')}
            </PublicCtaLink>
          </div>
        </section>
      )}

      <section className="acc-public-page-card">
        <h2>{t('pages.partnersApply.form.title')}</h2>
        <p>{t('pages.partnersApply.form.body')}</p>

        {formError && (
          <div className="acc-public-form-alert" role="alert">
            {formError}
          </div>
        )}

        <form className="acc-public-form" onSubmit={handleSubmit} noValidate>
          <div className="acc-public-form-grid">
            <div className="acc-public-form-field">
              <label htmlFor="partner-name">{t('pages.partnersApply.form.fields.name.label')}</label>
              <input
                id="partner-name"
                className={`acc-input${fieldErrors.name ? ' acc-input--error' : ''}`}
                value={form.name}
                onChange={updateField('name')}
                placeholder={t('pages.partnersApply.form.fields.name.placeholder')}
                aria-invalid={!!fieldErrors.name}
                aria-describedby={fieldErrors.name ? 'partner-name-error' : undefined}
              />
              {fieldErrors.name && <span id="partner-name-error" className="acc-public-form-error">{fieldErrors.name}</span>}
            </div>

            <div className="acc-public-form-field">
              <label htmlFor="partner-company">{t('pages.partnersApply.form.fields.company.label')}</label>
              <input
                id="partner-company"
                className={`acc-input${fieldErrors.company ? ' acc-input--error' : ''}`}
                value={form.company}
                onChange={updateField('company')}
                placeholder={t('pages.partnersApply.form.fields.company.placeholder')}
                aria-invalid={!!fieldErrors.company}
                aria-describedby={fieldErrors.company ? 'partner-company-error' : undefined}
              />
              {fieldErrors.company && (
                <span id="partner-company-error" className="acc-public-form-error">{fieldErrors.company}</span>
              )}
            </div>

            <div className="acc-public-form-field">
              <label htmlFor="partner-email">{t('pages.partnersApply.form.fields.email.label')}</label>
              <input
                id="partner-email"
                type="email"
                className={`acc-input${fieldErrors.email ? ' acc-input--error' : ''}`}
                value={form.email}
                onChange={updateField('email')}
                placeholder={t('pages.partnersApply.form.fields.email.placeholder')}
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'partner-email-error' : undefined}
              />
              {fieldErrors.email && (
                <span id="partner-email-error" className="acc-public-form-error">{fieldErrors.email}</span>
              )}
            </div>

            <div className="acc-public-form-field">
              <label htmlFor="partner-country">{t('pages.partnersApply.form.fields.country.label')}</label>
              <input
                id="partner-country"
                className="acc-input"
                value={form.country}
                onChange={updateField('country')}
                placeholder={t('pages.partnersApply.form.fields.country.placeholder')}
              />
            </div>

            <div className="acc-public-form-field">
              <label htmlFor="partner-website">{t('pages.partnersApply.form.fields.websiteUrl.label')}</label>
              <input
                id="partner-website"
                type="url"
                className={`acc-input${fieldErrors.websiteUrl ? ' acc-input--error' : ''}`}
                value={form.websiteUrl}
                onChange={updateField('websiteUrl')}
                placeholder={t('pages.partnersApply.form.fields.websiteUrl.placeholder')}
                aria-invalid={!!fieldErrors.websiteUrl}
                aria-describedby={fieldErrors.websiteUrl ? 'partner-website-error' : undefined}
              />
              {fieldErrors.websiteUrl && (
                <span id="partner-website-error" className="acc-public-form-error">{fieldErrors.websiteUrl}</span>
              )}
            </div>

            <div className="acc-public-form-field">
              <label htmlFor="partner-linkedin">{t('pages.partnersApply.form.fields.linkedinUrl.label')}</label>
              <input
                id="partner-linkedin"
                type="url"
                className={`acc-input${fieldErrors.linkedinUrl ? ' acc-input--error' : ''}`}
                value={form.linkedinUrl}
                onChange={updateField('linkedinUrl')}
                placeholder={t('pages.partnersApply.form.fields.linkedinUrl.placeholder')}
                aria-invalid={!!fieldErrors.linkedinUrl}
                aria-describedby={fieldErrors.linkedinUrl ? 'partner-linkedin-error' : undefined}
              />
              {fieldErrors.linkedinUrl && (
                <span id="partner-linkedin-error" className="acc-public-form-error">{fieldErrors.linkedinUrl}</span>
              )}
            </div>

            <div className="acc-public-form-field">
              <label htmlFor="partner-type">{t('pages.partnersApply.form.fields.partnerType.label')}</label>
              <select
                id="partner-type"
                className="acc-select"
                value={form.partnerType}
                onChange={updateField('partnerType')}
              >
                <option value="">{t('pages.partnersApply.form.fields.partnerType.placeholder')}</option>
                {Object.entries(partnerTypes).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>

            <div className="acc-public-form-field acc-public-form-field--span-2">
              <label htmlFor="partner-audience">{t('pages.partnersApply.form.fields.audienceDescription.label')}</label>
              <textarea
                id="partner-audience"
                className="acc-input acc-public-form-textarea"
                value={form.audienceDescription}
                onChange={updateField('audienceDescription')}
                placeholder={t('pages.partnersApply.form.fields.audienceDescription.placeholder')}
              />
            </div>

            <div className="acc-public-form-field acc-public-form-field--span-2">
              <label htmlFor="partner-plan">{t('pages.partnersApply.form.fields.howTheyPlanToSell.label')}</label>
              <textarea
                id="partner-plan"
                className="acc-input acc-public-form-textarea"
                value={form.howTheyPlanToSell}
                onChange={updateField('howTheyPlanToSell')}
                placeholder={t('pages.partnersApply.form.fields.howTheyPlanToSell.placeholder')}
              />
            </div>

            <div className="acc-public-form-field acc-public-form-field--span-2">
              <label htmlFor="partner-notes">{t('pages.partnersApply.form.fields.notes.label')}</label>
              <textarea
                id="partner-notes"
                className="acc-input acc-public-form-textarea"
                value={form.notes}
                onChange={updateField('notes')}
                placeholder={t('pages.partnersApply.form.fields.notes.placeholder')}
              />
            </div>
          </div>

          <div className="acc-public-form-actions">
            <button type="submit" className="acc-public-button acc-public-button--primary" disabled={submitting}>
              {submitting ? t('pages.partnersApply.form.submitting') : t('pages.partnersApply.form.submit')}
            </button>
          </div>
        </form>
      </section>
    </PublicContentPage>
  );
}
